/**
 * SystemDox's own icon family — the brand-carrying glyphs for the bottom nav
 * and the hub "Where to?" diagram.
 *
 * These replace the stock lucide set in the two places users actually form an
 * impression of the product's identity. The family rules that make them read
 * as one set (and not another AI-generated lucide app):
 *
 *  - 24×24 grid, 2px rounded strokes, `currentColor` — drop-in for lucide
 *    sizing/colour conventions, so call sites only swap the import.
 *  - one filled "context node" dot per glyph, placed where it means something:
 *    the door of Home, the bullet of Docs, the gap Learn surfaces, the beacon
 *    over the Knowledge Base, the antenna tip of the agent, the near end of the
 *    Connect link, the first item on Plan's board. The dot is the SystemDox
 *    motif — a piece of structured context — and the visual signature that
 *    ties the set together. (Add is the one exception: its plus IS the accent.)
 *
 * Utility glyphs (arrows, close, chevrons) stay lucide on purpose — wayfinding
 * symbols should look exactly like everyone else's.
 */

import type { ReactNode } from 'react';

export type SdxIconProps = { className?: string };

function Base({ className, children }: SdxIconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      {children}
    </svg>
  );
}

/** A filled context-node dot — the family's signature accent. */
function Dot({ cx, cy, r = 1.5 }: { cx: number; cy: number; r?: number }) {
  return <circle cx={cx} cy={cy} r={r} fill="currentColor" stroke="none" />;
}

/** House with the node as its door — the hub is where context lives. */
export function IconHome({ className }: SdxIconProps) {
  return (
    <Base className={className}>
      <path d="M4.75 11.4 12 4.9l7.25 6.5" />
      <path d="M6.6 10.2v7.45a1.75 1.75 0 0 0 1.75 1.75h7.3a1.75 1.75 0 0 0 1.75-1.75V10.2" />
      <Dot cx={12} cy={15.4} r={1.6} />
    </Base>
  );
}

/** New document — page with a fold and a plus (the plus is the accent). */
export function IconAdd({ className }: SdxIconProps) {
  return (
    <Base className={className}>
      <path d="M13.75 3.25H7.5A1.75 1.75 0 0 0 5.75 5v14a1.75 1.75 0 0 0 1.75 1.75h9.5A1.75 1.75 0 0 0 18.75 19V8.25Z" />
      <path d="M13.75 3.25V8.25h5" />
      <path d="M12.25 11.75v5" />
      <path d="M9.75 14.25h5" />
    </Base>
  );
}

/** Voice capture — mic whose base is the node: input becomes context. */
export function IconCapture({ className }: SdxIconProps) {
  return (
    <Base className={className}>
      <rect x="9.25" y="3" width="5.5" height="9.5" rx="2.75" />
      <path d="M6.25 11.25a5.75 5.75 0 0 0 11.5 0" />
      <path d="M12 17v3" />
      <Dot cx={12} cy={21.3} r={1.6} />
    </Base>
  );
}

/** The doc library — stacked pages, first line bulleted by the node. */
export function IconDocs({ className }: SdxIconProps) {
  return (
    <Base className={className}>
      <path d="M9 6.5V4.75A1.75 1.75 0 0 1 10.75 3h6.5A1.75 1.75 0 0 1 19 4.75V16" />
      <rect x="5.5" y="6.5" width="11.5" height="14" rx="1.75" />
      <Dot cx={8.75} cy={11.5} />
      <path d="M11.5 11.5h3" />
      <path d="M8.75 15h5.75" />
    </Base>
  );
}

/** Gap analysis — a magnifier over a broken line; the node is the gap found. */
export function IconGap({ className }: SdxIconProps) {
  return (
    <Base className={className}>
      <circle cx="10.75" cy="10.75" r="6.25" />
      <path d="m15.3 15.3 4.2 4.2" />
      <path d="M7.6 12.4h2.1" />
      <path d="M12 12.4h2.1" />
      <Dot cx={10.85} cy={9.3} />
    </Base>
  );
}

/** The published knowledge base — an open book under a shared beacon node. */
export function IconKnowledgeBase({ className }: SdxIconProps) {
  return (
    <Base className={className}>
      <path d="M12 6.75C10.3 5.3 8 4.75 5.25 4.75v12.5c2.75 0 5.05.55 6.75 2 1.7-1.45 4-2 6.75-2V4.75C16 4.75 13.7 5.3 12 6.75Z" />
      <path d="M12 6.75v12.5" />
      <Dot cx={12} cy={2.6} />
    </Base>
  );
}

/** AI agents — a chip face whose antenna carries the node it reads/writes. */
export function IconAgents({ className }: SdxIconProps) {
  return (
    <Base className={className}>
      <rect x="5.5" y="7.5" width="13" height="10.5" rx="2.5" />
      <path d="M12 7.5V5.5" />
      <Dot cx={12} cy={3.9} />
      <path d="M9.75 11.5v2.25" />
      <path d="M14.25 11.5v2.25" />
    </Base>
  );
}

/** The plan board — a clipboard whose first item is the node: context
 *  becomes scoped, ordered work. */
export function IconPlan({ className }: SdxIconProps) {
  return (
    <Base className={className}>
      <path d="M15 4.5h2.25A1.75 1.75 0 0 1 19 6.25v13A1.75 1.75 0 0 1 17.25 21H6.75A1.75 1.75 0 0 1 5 19.25v-13A1.75 1.75 0 0 1 6.75 4.5H9" />
      <rect x="9" y="2.75" width="6" height="3.5" rx="1" />
      <Dot cx={8.9} cy={11.75} />
      <path d="M11.75 11.75h3.35" />
      <path d="M8.9 15.75h6.2" />
    </Base>
  );
}

/** MCP connection — two systems linked; the near node is filled (yours). */
export function IconConnect({ className }: SdxIconProps) {
  return (
    <Base className={className}>
      <circle cx="17.25" cy="6.75" r="2.9" />
      <path d="m8.85 15.15 6.3-6.3" />
      <Dot cx={6.75} cy={17.25} r={2.4} />
    </Base>
  );
}
